<script setup lang="ts">
  import { IItemOption } from '@/interfaces/IItemOption';
  import { IOption } from '@/interfaces/IOption';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { whenever } from '@vueuse/core';
  import { ref, computed, watch } from 'vue';
  import StoreMenuItemOptionsBlockItem from './StoreMenuItemOptionsBlockItem.vue';

  const props = defineProps<{
    option: IOption | IItemOption;
    value: Record<number, number> | undefined;
    onlyDisplay?: boolean;
  }>();

  const emits = defineEmits(['input']);

  const expanded = ref(true);

  const finalOption = computed(() => {
    if ('option' in props.option) {
      const o = {
        ...props.option.option!,
      };
      const iO = props.option;
      o.mandatory = iO.mandatory ?? o.mandatory;
      o.multiple = iO.multiple ?? o.multiple;
      o.min = iO.min ?? o.min;
      o.max = iO.max ?? o.max;
      o.active = iO.active ?? o.active;

      return o;
    } else {
      return props.option;
    }
  });

  const values = computed({
    get: () => props.value ?? {},
    set: (value: Record<number, number>) => {
      emits('input', value);
    },
  });

  const initialized = ref(false);

  watch(
    finalOption,
    () => {
      // Delete all values
      const newValues: Record<number, number> = {};
      for (const item of finalOption.value.optionItems ?? []) {
        newValues[item.id!] = 0;
      }

      if (values.value) {
        values.value = {
          ...newValues,
          ...values.value,
        };
      }

      initialized.value = true;
    },
    { immediate: true }
  );

  const hasValue = computed(() => {
    return !initialized.value
      ? false
      : Object.values(values.value).some((v) => v > 0);
  });

  const totalSelected = computed(() => {
    return Object.values(values.value).reduce((acc, v) => acc + v, 0);
  });

  const min = computed(() => {
    const fO = finalOption.value;

    if (fO.main) {
      return 1;
    }

    if (fO.mandatory) {
      return fO.min ?? 1;
    }

    return fO.min ?? 0;
  });

  const max = computed(() => {
    if (!finalOption.value.multiple) return 1;
    return finalOption.value.max ?? Infinity;
  });

  const mandatory = computed(() => {
    return (
      finalOption.value.main || finalOption.value.mandatory || min.value > 0
    );
  });

  const leftToSelect = computed(() => {
    if (max.value === Infinity) return undefined;

    return max.value - totalSelected.value;
  });

  const isFull = computed(() => {
    if (!finalOption.value.multiple) {
      return totalSelected.value > 0;
    }

    return (
      (finalOption.value.max ?? null) !== null &&
      totalSelected.value >= finalOption.value.max
    );
  });

  const minAchieved = computed(() => {
    return totalSelected.value >= min.value;
  });

  whenever(isFull, () => {
    if (finalOption.value.main || !finalOption.value.multiple) {
      expanded.value = false;
    }
  });

  function shouldDisableItem(item: IOptionItem) {
    const fO = finalOption.value;

    if (fO.multiple) {
      if (isFull.value && values.value[item.id!] === 0) return true;
    }

    return false;
  }

  const subtitle = computed(() => {
    if (!finalOption.value.multiple) return null;

    if (finalOption.value.max && finalOption.value.min) {
      if (finalOption.value.max !== finalOption.value.min)
        return `Seleccione entre ${finalOption.value.min} y ${finalOption.value.max}`;
      else return `Seleccione ${finalOption.value.min}`;
    } else if (finalOption.value.max)
      return `Seleccione hasta ${finalOption.value.max}`;
    else if (finalOption.value.min)
      return `Seleccione al menos ${finalOption.value.min}`;

    return null;
  });

  function set(id: number, value: number) {
    if (!finalOption.value.multiple) {
      values.value = {
        [id]: value,
      };
    } else {
      values.value[id] = value;
    }
  }
</script>

<template>
  <div
    class="store-item-option"
    :class="{
      collapsed: !expanded,
      mandatory,
      'has-value': hasValue,
      'min-achieved': minAchieved && hasValue,
      'only-display': onlyDisplay,
    }"
  >
    <div class="header" @click="expanded = !expanded">
      <div class="title-container">
        <span class="block-title">{{ finalOption.title }}</span>
        <div v-if="finalOption.description" class="block-description">
          {{ finalOption.description }}
        </div>
        <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
      </div>
      <div class="spacer"></div>
      <template v-if="mandatory && !minAchieved">
        <v-chip small color="orange" text-color="white" class="mandatory-badge">
          Obligatorio
        </v-chip>
      </template>
      <v-chip
        v-else-if="minAchieved && hasValue"
        small
        color="green"
        text-color="white"
        class="mandatory-badge"
      >
        <v-icon small>mdi-check</v-icon>
      </v-chip>
      <v-icon small class="expand-icon">
        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </div>
    <v-expand-transition>
      <div v-if="finalOption.optionItems && initialized" class="items">
        <StoreMenuItemOptionsBlockItem
          class="item"
          v-for="item in finalOption.optionItems"
          :key="item.id"
          :value="values[item.id]"
          @input="set(item.id, $event)"
          :option-item="item"
          :parent="finalOption"
          :disabled="shouldDisableItem(item)"
          :left-to-select="leftToSelect"
          :only-display="onlyDisplay"
        />
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
  .store-item-option {
    .header {
      @apply tw-flex tw-items-center tw-space-x-4 tw-cursor-pointer tw-px-4 tw-py-4 tw-tracking-wide;
      @apply hover:tw-bg-gray-50;

      .title-container {
        @apply tw-flex tw-flex-col;

        .block-title {
          @apply tw-font-semibold tw-text-gray-600;
        }

        .block-description {
          @apply tw-text-sm tw-text-gray-500;
        }

        .subtitle {
          @apply tw-text-sm tw-text-gray-500;
        }
      }

      .expand-icon {
        @apply tw-text-gray-500;
      }
    }

    .items {
      @apply tw-divide-y tw-transition-all tw-transform-gpu tw-h-auto tw-overflow-hidden;
      @apply tw-ring-1 tw-rounded-md tw-ring-gray-200;

      .item {
      }
    }

    &.collapsed {
      .header {
        @apply tw-bg-gray-50;
      }

      .items {
        @apply tw-overflow-hidden tw-h-0 tw-opacity-0;
      }
    }

    &.mandatory {
      .title {
        @apply tw-text-black;
      }
      .items {
        @apply tw-ring-2 tw-ring-orange-300;
      }
    }

    &.min-achieved {
      .items {
        @apply tw-ring-2 tw-ring-green-300;
      }
    }

    &.only-display {
      .items {
        @apply tw-ring-1 tw-ring-gray-200;
      }
    }
  }
</style>
